import React, { useState, useEffect, useMemo } from 'react'
import {
  Button,
  Col,
  Form,
  Nav,
  OverlayTrigger,
  Popover,
  Row,
  Tab,
  Tabs,
} from 'react-bootstrap'
import { pickerTheme } from 'designer/component/system/themes/elementTheme'
import { ThemeProvider } from '@mui/material/styles'

import LocalizationProvider from '@mui/lab/LocalizationProvider'
import thLocale from 'date-fns/locale/th'

import OverwriteFnsBE from './OverwriteFnsBE'
import OverwriteFnsAD from './OverwriteFnsAD'

import Box from '@mui/material/Box'
import DesktopDateTimePicker from '@mui/lab/DateTimePicker'
import FeatherIcon from 'feather-icons-react'
import InputMask from 'react-input-mask'

import classNames from 'classnames/bind'
import './datepicker-custom-styles.css'

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import TimePickerCustom from '../TimePicker/TimePickerCustom'
import moment from 'moment'
import StaticDatePickerCustom from './StaticDatePickerCustom'

dayjs.extend(customParseFormat)

const FORMAT_DATETIME = 'YYYY-MM-DD HH:mm:ss'

const CalendarTimePickerCustom = (props) => {
  let {
    dateAdapter,
    locale,
    value,
    onChange,
    placeholder,
    formatDisplay,
    isClearable,
    isError,
    readonly,
    calendar,
    ampm,
    dateFormat,
    timeFormat,
    style,
    id,
    name,
    handleKeyDown = () => {},
    isTable
  } = props

  let regexAlphabet = /^[a-zA-Z]+$/
  let { formatDate, formatDateTime } = useTimeFormat()
  let date = value ? moment(value).format('YYYY-MM-DD') : null

  const formatDateToMask = (formatDsp = '') => {
    let mask = formatDsp.split('').reduce((items, item) => {
      if (regexAlphabet.test(item)) {
        items += 9
      } else {
        items += item
      }
      return items
    }, '')
    return mask
  }

  const formatTimeToMask = (formatDsp = '') => {
    let mask = formatDsp.split('').reduce((items, item) => {
      if (item === 'A') {
        items += 'aa'
      } else if (regexAlphabet.test(item)) {
        items += 9
      } else {
        items += item
      }
      return items
    }, '')
    return mask
  }

  let formatDsp = dateFormat + ' ' + timeFormat || ''

  let maskDateInput = useMemo(() => formatDateToMask(dateFormat), [dateFormat])
  let maskTimeInput = useMemo(() => formatTimeToMask(timeFormat), [timeFormat])

  let maskInput = maskDateInput + ' ' + maskTimeInput

  const convertDateTime = (fullDate, format = formatDsp) => {
    if (fullDate) {
      const date = dayjs(fullDate)
      if (date.isValid()) {
        return date.format(format)
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  const convertTime = (time, format = 'HH:mm:ss') => {
    if (time) {
      const date = dayjs(time)
      if (date.isValid()) {
        return date.format(format)
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  const [dateValue, setDateValue] = useState(
    value ? convertDateTime(value) : ''
  )
  const [dateText, setDateText] = useState(
    value ? dayjs(value).format(formatDsp) : ''
  )

  const [views, setViews] = useState(['hours', 'minutes', 'seconds'])

  const [timeValue, setTimeValue] = useState(
    value ? convertTime(value, 'HH:mm:ss') : ''
  )

  const [showPicker, setShowPicker] = useState(false)
  const [selectTab, setSelectTab] = useState('date')

  useEffect(() => {
    setTimeValue(convertTime(value, 'HH:mm:ss'))
  }, [showPicker])

  const handleChangeTime = (timeValue) => {
    let dateDefault = dayjs(new Date(dateValue)).format('YYYY-MM-DD')
    let dateTimeFormat = `${dateDefault} ${timeValue}`
    let dateTimeValue = new Date(dateTimeFormat)
    setTimeValue(timeValue)
    if (onChange) {
      onChange({ value: dateTimeValue, format: dateTimeFormat })
    }
  }

  const handleTogglePicker = () => {
    setShowPicker(!showPicker)
    setSelectTab('date')
  }

  const timePicker = (time) => {
    return (
      <Popover className="popover-timepicker">
        <Popover.Content className="pt-0">
          <Tabs
            id="controlled-tab-example"
            activeKey={selectTab}
            onSelect={(selectedKey) => setSelectTab(selectedKey)}
            className="mb-3"
            fill
          >
            <Tab eventKey="date" title={<FeatherIcon icon="calendar" />}>
              <StaticDatePickerCustom
                {...props}
                calendar={calendar}
                dateText={dateText}
                setSelectTab={setSelectTab}
              />
            </Tab>
            <Tab eventKey="time" title={<FeatherIcon icon="clock" />}>
              <TimePickerCustom
                value={time}
                onChange={(value) => handleChangeTime(value)}
                onClose={handleTogglePicker}
                formatDisplay={timeFormat}
                ampm={ampm}
                timeValue={timeValue}
                setTimeValue={setTimeValue}
                convertTime={convertTime}
              />
            </Tab>
          </Tabs>
        </Popover.Content>
      </Popover>
    )
  }

  const convertDate = (data, input = false) => {
    if (data) {
      const replaceForm = formatDsp.replaceAll('B', 'Y')
      const date = dayjs(data, replaceForm)
      if (date.isValid()) {
        const yearAdd = input && formatDsp.search('B') !== -1 ? 543 : 0
        const ceYear = date.year() - yearAdd
        const ceDate = `${ceYear}${date.format('-MM-DD')}`
        return dayjs(ceDate).format('YYYY-MM-DD')
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  const handleChangeDateText = (e) => {
    let { value } = e.target
    setDateText(value)
    if (value.length !== maskInput.length) return
    let [date, time] = value.split(' ')
    let dateConvert = convertDate(value, dateFormat)
    let dateTime = [dateConvert, time].join(' ')
    if (dayjs(dateTime).isValid()) {
      let dayjsDate = dayjs(dateTime)
      let dateFormat = dayjsDate.format(FORMAT_DATETIME)
      let dateValue = dayjsDate.format(FORMAT_DATETIME)
      setDateValue(dateValue)
      if (onChange) {
        onChange({ value: dateValue, format: dateFormat })
      }
    }
  }

  const handleChangeDate = (date) => {
    let dateFormat = dayjs(date).format(FORMAT_DATETIME)
    let dateDisplay = dayjs(date).format(formatDsp)

    setDateValue(date)
    setDateText(dateDisplay)

    if (onChange) {
      onChange({ value: date, format: dateFormat })
    }
  }

  const clearDate = () => {
    setDateValue('')
    setDateText('')

    if (onChange) {
      onChange({})
    }
  }

  useEffect(() => {
    if (value) {
      setDateText(dayjs(value).format(formatDsp))
      setDateValue(convertDateTime(dayjs(value)))
    } else {
      setDateText('')
      setDateValue('')
    }
  }, [value, formatDsp, calendar])

  useEffect(() => {
    let viewHMS = /(hh:mm:ss|HH:mm:ss)/
    let viewHM = /(hh:mm|HH:mm)/
    let viewMS = /(mm:ss)/
    let viewH = /(hh|HH)/
    let viewM = /(mm)/
    let viewS = /(ss)/
    if (viewHMS.test(formatDsp)) {
      setViews(['year', 'month', 'day', 'hours', 'minutes', 'seconds'])
    } else if (viewHM.test(formatDsp)) {
      setViews(['year', 'month', 'day', 'hours', 'minutes'])
    } else if (viewMS.test(formatDsp)) {
      setViews(['year', 'month', 'day', 'minutes', 'seconds'])
    } else if (viewH.test(formatDsp)) {
      setViews(['year', 'month', 'day', 'hours'])
    } else if (viewM.test(formatDsp)) {
      setViews(['year', 'month', 'day', 'minutes'])
    } else if (viewS.test(formatDsp)) {
      setViews(['year', 'month', 'day', 'seconds'])
    } else {
      setViews(['year', 'month', 'day', 'hours', 'minutes', 'seconds'])
    }
    if (value) {
      setDateText(convertDateTime(value, formatDsp))
      setDateValue(dayjs(value))
    } else {
      setDateText('')
      setDateValue('')
    }
  }, [value, formatDsp, ampm])

  if (readonly) {
    return isTable ? (
      dateText
    ) : (
      <Form.Control readOnly id={id} name={name} value={dateText} />
    )
  }

  return (
    <LocalizationProvider
      dateAdapter={
        dateAdapter ?? calendar === 'BE' ? OverwriteFnsBE : OverwriteFnsAD
      }
      locale={locale ?? thLocale}
    >
      <ThemeProvider theme={pickerTheme}>
        <DesktopDateTimePicker
          id={id}
          name={name}
          views={views}
          value={dateValue}
          readOnly={readonly}
          onChange={(newValue) => {
            handleChangeDate(newValue)
          }}
          ampm={ampm}
          renderInput={({ inputRef, inputProps, InputProps }) => {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <div className="position-relative w-100">
                  <InputMask
                    {...inputProps}
                    id={`${id}-input`}
                    name={`${name}-input`}
                    value={dateText}
                    onChange={handleChangeDateText}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder ?? formatDsp}
                    mask={maskInput}
                    maskChar={'_'}
                    disabled={readonly}
                    style={style}
                  >
                    {(inputMaskProps) => {
                      return (
                        <input
                          className={classNames('form-control', {
                            'input-date pr': isClearable,
                            'is-invalid': isError,
                          })}
                          ref={inputRef}
                          {...inputMaskProps}
                        />
                      )
                    }}
                  </InputMask>

                  {isClearable && dateText && (
                    <button className="clear-btn" onClick={() => clearDate()}>
                      <i
                        className={classNames('fas fa-times clearable-btn', {
                          error: isError,
                        })}
                      />
                    </button>
                  )}
                </div>
                <div>
                  <OverlayTrigger
                    trigger="click"
                    placement="bottom"
                    overlay={timePicker(value)}
                    rootClose={true}
                    show={showPicker} // Controlled by state
                    onToggle={(nextShow) => setShowPicker(nextShow)}
                  >
                    <button className="clock-btn" onClick={handleTogglePicker}>
                      <FeatherIcon
                        className={'text-secondary'}
                        icon="calendar"
                        size="24"
                      />
                    </button>
                  </OverlayTrigger>
                </div>
              </Box>
            )
          }}
          {...props}
        />
      </ThemeProvider>
    </LocalizationProvider>
  )
}

export default CalendarTimePickerCustom
